import Layout from '@/components/Layout';
import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useCloudFunction } from '@/hooks/use-cloud-function';
import { useIntegration } from '@/hooks/use-integration';
import { useSelectedLocation } from '@/hooks/use-selected-location';
import {
  fadeIn,
  glassCard,
  iconHover,
  pageTransition,
  staggerContainer,
  textReveal,
} from '@/lib/animations';
import { motion } from 'framer-motion';
import { Bell, Cloud, Globe, MapPin, Palette, Shield } from 'lucide-react';
import { useState } from 'react';

const Settings = () => {
  const { theme, setTheme } = useTheme();
  const { triggerAuthenticate } = useCloudFunction();
  const { partner, endpoints } = useIntegration();
  const {
    selectedLocationId,
    selectedLocationName,
    locations,
    locationsLoading,
    handleLocationSelect,
    hasSelectedLocation,
    hasLocations,
  } = useSelectedLocation();
  const [enableAnimations, setEnableAnimations] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [dataCollection, setDataCollection] = useState(false);
  const [usageAnalytics, setUsageAnalytics] = useState(false);
  const [language, setLanguage] = useState('English');
  const [testingAuthenticate, setTestingAuthenticate] = useState(false);

  const handleTestAuthenticate = async () => {
    setTestingAuthenticate(true);
    try {
      const result = await triggerAuthenticate();
      console.log('Test authenticate result:', result);
      if (result.success) {
        alert('✅ Authenticate cloud function called successfully!');
      } else {
        alert(`❌ Authenticate failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Test authenticate error:', error);
      alert(
        `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setTestingAuthenticate(false);
    }
  };

  const settingsSections = [
    {
      title: 'Appearance',
      subtitle: 'Customize how the application looks',
      icon: Palette,
      color: 'from-purple-500 to-purple-600',
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Theme</p>
              <p className="text-muted-foreground text-sm">
                Choose your preferred theme
              </p>
            </div>
            <Select
              value={theme}
              onValueChange={(value: 'light' | 'dark' | 'system') =>
                setTheme(value)
              }>
              <SelectTrigger className="w-32 glass-button text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border-border">
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Enable Animations</p>
              <p className="text-muted-foreground text-sm">
                Turn on/off UI animations
              </p>
            </div>
            <Switch
              checked={enableAnimations}
              onCheckedChange={setEnableAnimations}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Notifications',
      subtitle: 'Manage your notification preferences',
      icon: Bell,
      color: 'from-blue-500 to-blue-600',
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Email Notifications</p>
              <p className="text-muted-foreground text-sm">
                Receive updates via email
              </p>
            </div>
            <Switch
              checked={emailNotifications}
              onCheckedChange={setEmailNotifications}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Push Notifications</p>
              <p className="text-muted-foreground text-sm">
                Receive push notifications
              </p>
            </div>
            <Switch
              checked={pushNotifications}
              onCheckedChange={setPushNotifications}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Privacy',
      subtitle: 'Manage your privacy settings',
      icon: Shield,
      color: 'from-green-500 to-green-600',
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Data Collection</p>
              <p className="text-muted-foreground text-sm">
                Allow data collection for improvements
              </p>
            </div>
            <Switch
              checked={dataCollection}
              onCheckedChange={setDataCollection}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Usage Analytics</p>
              <p className="text-muted-foreground text-sm">
                Help improve the app with usage data
              </p>
            </div>
            <Switch
              checked={usageAnalytics}
              onCheckedChange={setUsageAnalytics}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Language',
      subtitle: 'Choose your preferred language',
      icon: Globe,
      color: 'from-orange-500 to-orange-600',
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Language</p>
              <p className="text-muted-foreground text-sm">
                Select your preferred language
              </p>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-32 glass-button text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border-border">
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="French">French</SelectItem>
                <SelectItem value="German">German</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ),
    },
    {
      title: 'Location',
      subtitle: 'Select your preferred location',
      icon: MapPin,
      color: 'from-red-500 to-red-600',
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground font-medium">Default Location</p>
              <p className="text-muted-foreground text-sm">
                Choose your default location for appointments
              </p>
            </div>
            <Select
              value={selectedLocationId || ''}
              onValueChange={handleLocationSelect}
              disabled={locationsLoading || !hasLocations}>
              <SelectTrigger className="w-32 glass-button text-foreground">
                <SelectValue placeholder="Select Location" />
              </SelectTrigger>
              <SelectContent className="bg-background border-border">
                {locationsLoading ? (
                  <SelectItem value="loading" disabled>
                    Loading locations...
                  </SelectItem>
                ) : hasLocations ? (
                  locations.map((location) => (
                    <SelectItem
                      key={location.locationId}
                      value={location.locationId}>
                      {location.locationName}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none" disabled>
                    No locations found
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      ),
    },
    {
      title: 'Integration Test',
      subtitle: 'Test integration cloud functions',
      icon: Cloud,
      color: 'from-indigo-500 to-indigo-600',
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-foreground font-medium">
              Authenticate Cloud Function
            </p>
            <p className="text-muted-foreground text-sm">
              Test the authenticate cloud function manually
            </p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Partner: {partner?.name || 'None'}</p>
              <p>Endpoints: {endpoints.length}</p>
              <p>
                Cloud Secret ID:{' '}
                {partner?.cloud_secret_id ? 'Available' : 'Missing'}
              </p>
            </div>
            <Button
              onClick={handleTestAuthenticate}
              disabled={
                testingAuthenticate ||
                !partner?.cloud_secret_id ||
                endpoints.length === 0
              }
              className="w-full glass-button">
              {testingAuthenticate ? 'Testing...' : 'Test Authenticate'}
            </Button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <motion.div
        className="w-full pt-20 md:pt-24 px-4 md:px-6"
        {...pageTransition}>
        {/* Header */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="initial"
          animate="animate">
          <motion.h1
            className="text-3xl font-bold text-foreground mb-2"
            variants={textReveal}>
            Settings
          </motion.h1>
          <motion.p className="text-muted-foreground" variants={textReveal}>
            Customize your application preferences
          </motion.p>
        </motion.div>

        {/* Settings Grid */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          variants={staggerContainer}
          initial="initial"
          animate="animate">
          {settingsSections.map((section, index) => (
            <motion.div key={index} variants={glassCard}>
              <GlassCard>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <motion.div
                      whileHover={iconHover}
                      className={`w-10 h-10 rounded-2xl bg-gradient-to-r ${section.color} flex items-center justify-center`}>
                      <section.icon className="w-5 h-5 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        className="text-lg font-semibold text-foreground"
                        variants={textReveal}>
                        {section.title}
                      </motion.h2>
                      <motion.p
                        className="text-muted-foreground text-sm"
                        variants={textReveal}>
                        {section.subtitle}
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    animate="animate">
                    {section.content}
                  </motion.div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </Layout>
  );
};

export default Settings;
